import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Briefcase,
  MapPin,
  Clock,
  Users,
  Eye,
  Mail,
  Phone,
  Calendar,
  ExternalLink,
  Filter,
  Download,
  User,
  X
} from 'lucide-react';
import MainLayout from '../layouts/MainLayout';
import { Job, JobApplication } from '../types';
import * as careerService from '../services/careerService';
import { toast } from 'react-hot-toast';

const Career: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'jobs' | 'applications'>('jobs');
  
  // Jobs states
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'tr' | 'en' | 'es'>('tr');
  const [submitting, setSubmitting] = useState(false);
  const [jobSearchTerm, setJobSearchTerm] = useState('');

  // Job form states
  const [formTitles, setFormTitles] = useState({ tr: '', en: '', es: '' });
  const [formSubtitles, setFormSubtitles] = useState({ tr: '', en: '', es: '' });
  const [formLocations, setFormLocations] = useState({ tr: '', en: '', es: '' });
  const [formDescriptions, setFormDescriptions] = useState({ tr: '', en: '', es: '' });
  const [responsibilities, setResponsibilities] = useState({ tr: [''], en: [''], es: [''] });
  const [qualifications, setQualifications] = useState({ tr: [''], en: [''], es: [''] });
  const [advantages, setAdvantages] = useState({ tr: [''], en: [''], es: [''] });

  // Applications states
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);
  const [showApplicationDetails, setShowApplicationDetails] = useState(false);
  const [applicationSearchTerm, setApplicationSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    if (activeTab === 'jobs') {
      fetchJobs();
    } else {
      fetchApplications();
    }
  }, [activeTab]);

  const fetchJobs = async () => {
    try {
      setJobsLoading(true);
      const data = await careerService.getJobs();
      setJobs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('İş ilanları yüklenirken hata oluştu');
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      setApplicationsLoading(true);
      const data = await careerService.getJobApplications();
      setApplications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Başvurular yüklenirken hata oluştu');
      setApplications([]);
    } finally {
      setApplicationsLoading(false);
    }
  };

  // Job functions
  const handleNewJob = () => {
    setEditingJob(null);
    setFormTitles({ tr: '', en: '', es: '' });
    setFormSubtitles({ tr: '', en: '', es: '' });
    setFormLocations({ tr: '', en: '', es: '' });
    setFormDescriptions({ tr: '', en: '', es: '' });
    setResponsibilities({ tr: [''], en: [''], es: [''] });
    setQualifications({ tr: [''], en: [''], es: [''] });
    setAdvantages({ tr: [''], en: [''], es: [''] });
    setShowJobForm(true);
  };

  const handleEditJob = (job: Job) => {
    setEditingJob(job);
    setFormTitles({
      tr: job.title?.tr || '',
      en: job.title?.en || '',
      es: job.title?.es || ''
    });
    setFormSubtitles({
      tr: job.subtitle?.tr || '',
      en: job.subtitle?.en || '',
      es: job.subtitle?.es || ''
    });
    setFormLocations({
      tr: job.location?.tr || '',
      en: job.location?.en || '',
      es: job.location?.es || ''
    });
    setFormDescriptions({
      tr: job.description?.tr || '',
      en: job.description?.en || '',
      es: job.description?.es || ''
    });
    setResponsibilities({
      tr: job.responsibilities?.map(r => r.tr || '') || [''],
      en: job.responsibilities?.map(r => r.en || '') || [''],
      es: job.responsibilities?.map(r => r.es || '') || ['']
    });
    setQualifications({
      tr: job.required_qualifications?.map(q => q.tr || '') || [''],
      en: job.required_qualifications?.map(q => q.en || '') || [''],
      es: job.required_qualifications?.map(q => q.es || '') || ['']
    });
    setAdvantages({
      tr: job.advantages?.map(a => a.tr || '') || [''],
      en: job.advantages?.map(a => a.en || '') || [''],
      es: job.advantages?.map(a => a.es || '') || ['']
    });
    setShowJobForm(true);
  };

  const handleDeleteJob = async (job: Job) => {
    if (!window.confirm('Bu iş ilanını silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const jobId = job.id || job._id;
      if (!jobId) {
        toast.error('İş ilanı ID bulunamadı');
        return;
      }
      
      await careerService.deleteJob(jobId);
      toast.success('İş ilanı başarıyla silindi');
      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('İş ilanı silinirken hata oluştu');
    }
  };

  const handleCloseJobForm = () => {
    setShowJobForm(false);
    setEditingJob(null);
  };

  const handleJobSubmit = async (formData: FormData) => {
    try {
      setSubmitting(true);

      const jobData = {
        title: {
          tr: formData.get('title_tr') as string || '',
          en: formData.get('title_en') as string || '',
          es: formData.get('title_es') as string || ''
        },
        subtitle: {
          tr: formData.get('subtitle_tr') as string || '',
          en: formData.get('subtitle_en') as string || '',
          es: formData.get('subtitle_es') as string || ''
        },
        location: {
          tr: formData.get('location_tr') as string || '',
          en: formData.get('location_en') as string || '',
          es: formData.get('location_es') as string || ''
        },
        job_type: formData.get('job_type') as string || 'full-time',
        experience_level: formData.get('experience_level') as string || '',
        description: {
          tr: formData.get('description_tr') as string || '',
          en: formData.get('description_en') as string || '',
          es: formData.get('description_es') as string || ''
        },
        responsibilities: responsibilities.tr.filter(r => r.trim()).map((r, index) => ({
          tr: r,
          en: responsibilities.en[index] || '',
          es: responsibilities.es[index] || ''
        })),
        required_qualifications: qualifications.tr.filter(q => q.trim()).map((q, index) => ({
          tr: q,
          en: qualifications.en[index] || '',
          es: qualifications.es[index] || ''
        })),
        advantages: advantages.tr.filter(a => a.trim()).map((a, index) => ({
          tr: a,
          en: advantages.en[index] || '',
          es: advantages.es[index] || ''
        })),
        is_active: formData.get('is_active') === 'on'
      };

      if (editingJob) {
        const jobId = editingJob.id || editingJob._id;
        if (!jobId) {
          toast.error('İş ilanı ID bulunamadı');
          return;
        }
        await careerService.updateJob(jobId, jobData);
        toast.success('İş ilanı başarıyla güncellendi');
      } else {
        await careerService.createJob(jobData);
        toast.success('İş ilanı başarıyla oluşturuldu');
      }

      handleCloseJobForm();
      fetchJobs();
    } catch (error) {
      console.error('Error submitting job:', error);
      toast.error('İş ilanı kaydedilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };


  // Application functions
  const handleViewApplicationDetails = (application: JobApplication) => {
    setSelectedApplication(application);
    setShowApplicationDetails(true);
  };


  // Helper functions
  const addListItem = (setter: React.Dispatch<React.SetStateAction<{ tr: string[]; en: string[]; es: string[] }>>) => {
    setter(prev => ({
      tr: [...prev.tr, ''],
      en: [...prev.en, ''],
      es: [...prev.es, '']
    }));
  };

  const removeListItem = (setter: React.Dispatch<React.SetStateAction<{ tr: string[]; en: string[]; es: string[] }>>, index: number) => {
    setter(prev => ({
      tr: prev.tr.filter((_, i) => i !== index),
      en: prev.en.filter((_, i) => i !== index),
      es: prev.es.filter((_, i) => i !== index)
    }));
  };

  const updateListItem = (setter: React.Dispatch<React.SetStateAction<{ tr: string[]; en: string[]; es: string[] }>>, index: number, value: string, lang: 'tr' | 'en' | 'es') => {
    setter(prev => ({
      ...prev,
      [lang]: prev[lang].map((item, i) => i === index ? value : item)
    }));
  };


  // Filtered data
  const filteredJobs = jobs.filter(job => {
    const searchLower = jobSearchTerm.toLowerCase();
    return (
      job.title?.tr?.toLowerCase().includes(searchLower) ||
      job.title?.en?.toLowerCase().includes(searchLower) ||
      job.location?.tr?.toLowerCase().includes(searchLower) ||
      job.experience_level?.toLowerCase().includes(searchLower)
    );
  });

  const filteredApplications = applications.filter(application => {
    const matchesSearch = applicationSearchTerm === '' || 
      application.full_name.toLowerCase().includes(applicationSearchTerm.toLowerCase()) ||
      application.email.toLowerCase().includes(applicationSearchTerm.toLowerCase()) ||
      application.job?.title?.tr?.toLowerCase().includes(applicationSearchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || application.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Stats
  const activeJobs = jobs.filter(job => job.is_active).length;
  const getStatusCounts = () => {
    return {
      total: applications.length,
      pending: applications.filter(app => app.status === 'pending').length,
      reviewed: applications.filter(app => app.status === 'reviewed').length,
      interviewed: applications.filter(app => app.status === 'interviewed').length,
      accepted: applications.filter(app => app.status === 'accepted').length,
      rejected: applications.filter(app => app.status === 'rejected').length
    };
  };
  const statusCounts = getStatusCounts();

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kariyer Yönetimi</h1>
            <p className="text-gray-600">İş ilanları ve başvuruları yönetin</p>
          </div>
          {activeTab === 'jobs' && (
            <button
              onClick={handleNewJob}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Yeni İş İlanı
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('jobs')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'jobs'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Briefcase className="h-4 w-4 inline mr-2" />
              İş İlanları ({jobs.length})
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'applications'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="h-4 w-4 inline mr-2" />
              Başvurular ({applications.length})
            </button>
          </nav>
        </div>

        {/* Jobs Tab Content */}
        {activeTab === 'jobs' && (
          <>
            {/* Jobs Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Briefcase className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Toplam İlan</p>
                    <p className="text-2xl font-bold text-gray-900">{jobs.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Eye className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Aktif İlan</p>
                    <p className="text-2xl font-bold text-gray-900">{activeJobs}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Toplam Başvuru</p>
                    <p className="text-2xl font-bold text-gray-900">{applications.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Jobs Search */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="İş ilanlarında ara..."
                  value={jobSearchTerm}
                  onChange={(e) => setJobSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Jobs List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">İş İlanları</h2>
              </div>
              
              {jobsLoading ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Yükleniyor...</p>
                </div>
              ) : filteredJobs.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  {jobSearchTerm ? 'Arama kriterlerine uygun iş ilanı bulunamadı.' : 'Henüz iş ilanı bulunmuyor.'}
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredJobs.map((job) => (
                    <div key={job.id || job._id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h3 className="text-lg font-medium text-gray-900">
                              {job.title?.tr || 'Başlık'}
                            </h3>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              job.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {job.is_active ? 'Aktif' : 'Pasif'}
                            </span>
                          </div>
                          
                          <p className="text-gray-600 mt-1">{job.subtitle?.tr}</p>
                          
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 mr-1" />
                              {job.location?.tr}
                            </div>
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 mr-1" />
                              {job.job_type === 'full-time' ? 'Tam Zamanlı' :
                               job.job_type === 'part-time' ? 'Yarı Zamanlı' :
                               job.job_type === 'contract' ? 'Sözleşmeli' :
                               job.job_type === 'internship' ? 'Staj' :
                               job.job_type === 'hybrid' ? 'Hibrit' : job.job_type}
                            </div>
                            <div className="flex items-center">
                              <Briefcase className="h-4 w-4 mr-1" />
                              {job.experience_level}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          
                          
                          <button
                            onClick={() => handleEditJob(job)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Düzenle"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          
                          <button
                            onClick={() => handleDeleteJob(job)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Sil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Applications Tab Content */}
        {activeTab === 'applications' && (
          <>
            {/* Applications Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{statusCounts.total}</p>
                  <p className="text-sm text-gray-600">Toplam</p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-600">{statusCounts.pending}</p>
                  <p className="text-sm text-gray-600">Beklemede</p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{statusCounts.reviewed}</p>
                  <p className="text-sm text-gray-600">İncelendi</p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{statusCounts.interviewed}</p>
                  <p className="text-sm text-gray-600">Görüşme</p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{statusCounts.accepted}</p>
                  <p className="text-sm text-gray-600">Kabul</p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{statusCounts.rejected}</p>
                  <p className="text-sm text-gray-600">Red</p>
                </div>
              </div>
            </div>

            {/* Applications Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Ad, email veya pozisyon ara..."
                    value={applicationSearchTerm}
                    onChange={(e) => setApplicationSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">Tüm Durumlar</option>
                    <option value="pending">Beklemede</option>
                    <option value="reviewed">İncelendi</option>
                    <option value="interviewed">Görüşme Yapıldı</option>
                    <option value="accepted">Kabul Edildi</option>
                    <option value="rejected">Reddedildi</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Applications List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  Başvurular ({filteredApplications.length})
                </h2>
              </div>
              
              {applicationsLoading ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Yükleniyor...</p>
                </div>
              ) : filteredApplications.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  {applicationSearchTerm || statusFilter !== 'all' ? 'Filtrelere uygun başvuru bulunamadı.' : 'Henüz başvuru bulunmuyor.'}
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredApplications.map((application) => (
                    <div key={application.id || application._id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h3 className="text-lg font-medium text-gray-900">
                              {application.full_name}
                            </h3>
                          </div>
                          
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center text-sm text-gray-600">
                              <Briefcase className="h-4 w-4 mr-2" />
                              {application.job?.title?.tr || 'Pozisyon bilgisi yok'}
                            </div>
                            
                            <div className="flex items-center text-sm text-gray-600">
                              <Mail className="h-4 w-4 mr-2" />
                              {application.email}
                            </div>
                            
                            <div className="flex items-center text-sm text-gray-600">
                              <Phone className="h-4 w-4 mr-2" />
                              {application.phone}
                            </div>
                            
                            <div className="flex items-center text-sm text-gray-600">
                              <Calendar className="h-4 w-4 mr-2" />
                              {new Date(application.createdAt || '').toLocaleDateString('tr-TR')}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewApplicationDetails(application)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Detayları Görüntüle"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          
                          {application.cv_file && (
                            <a
                              href={application.cv_file}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                              title="CV İndir"
                            >
                              <Download className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Job Form Modal */}
      {showJobForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseJobForm}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <form onSubmit={(e) => { e.preventDefault(); handleJobSubmit(new FormData(e.target as HTMLFormElement)); }}>
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingJob ? 'İş İlanını Düzenle' : 'Yeni İş İlanı Oluştur'}
                </h2>
                <button
                  type="button"
                  onClick={handleCloseJobForm}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Language Tabs */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Dil Seçimi
                  </label>
                  <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                    {['tr', 'en', 'es'].map((lang) => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => setSelectedLanguage(lang as 'tr' | 'en' | 'es')}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                          selectedLanguage === lang
                            ? 'bg-white text-primary-600 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {lang === 'tr' ? 'Türkçe' : lang === 'en' ? 'English' : 'Español'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Hidden inputs for all languages */}
                <input type="hidden" name="title_tr" value={formTitles.tr} />
                <input type="hidden" name="title_en" value={formTitles.en} />
                <input type="hidden" name="title_es" value={formTitles.es} />
                <input type="hidden" name="subtitle_tr" value={formSubtitles.tr} />
                <input type="hidden" name="subtitle_en" value={formSubtitles.en} />
                <input type="hidden" name="subtitle_es" value={formSubtitles.es} />
                <input type="hidden" name="location_tr" value={formLocations.tr} />
                <input type="hidden" name="location_en" value={formLocations.en} />
                <input type="hidden" name="location_es" value={formLocations.es} />
                <input type="hidden" name="description_tr" value={formDescriptions.tr} />
                <input type="hidden" name="description_en" value={formDescriptions.en} />
                <input type="hidden" name="description_es" value={formDescriptions.es} />

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İş Başlığı 
                    <span className="ml-2 text-xs text-gray-500">
                      ({selectedLanguage === 'tr' ? 'Türkçe' : selectedLanguage === 'en' ? 'English' : 'Español'})
                    </span>
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={formTitles[selectedLanguage] || ''}
                    onChange={(e) => setFormTitles(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={selectedLanguage === 'tr' ? 'Örn: İnşaat Mühendisi' : selectedLanguage === 'en' ? 'e.g: Civil Engineer' : 'ej: Ingeniero Civil'}
                    required
                  />
                </div>

                {/* Subtitle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alt Başlık
                    <span className="ml-2 text-xs text-gray-500">
                      ({selectedLanguage === 'tr' ? 'Türkçe' : selectedLanguage === 'en' ? 'English' : 'Español'})
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formSubtitles[selectedLanguage] || ''}
                    onChange={(e) => setFormSubtitles(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={selectedLanguage === 'tr' ? 'Örn: Deneyimli İnşaat Mühendisi Aranıyor' : selectedLanguage === 'en' ? 'e.g: Experienced Civil Engineer Wanted' : 'ej: Se Busca Ingeniero Civil con Experiencia'}
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lokasyon
                    <span className="ml-2 text-xs text-gray-500">
                      ({selectedLanguage === 'tr' ? 'Türkçe' : selectedLanguage === 'en' ? 'English' : 'Español'})
                    </span>
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={formLocations[selectedLanguage] || ''}
                    onChange={(e) => setFormLocations(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={selectedLanguage === 'tr' ? 'Örn: İstanbul, Türkiye' : selectedLanguage === 'en' ? 'e.g: Istanbul, Turkey' : 'ej: Estambul, Turquía'}
                    required
                  />
                </div>

                {/* Job Type and Experience Level */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      İş Tipi <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="job_type"
                      defaultValue={editingJob?.job_type || 'full-time'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="full-time">Tam Zamanlı</option>
                      <option value="part-time">Yarı Zamanlı</option>
                      <option value="contract">Sözleşmeli</option>
                      <option value="internship">Staj</option>
                      <option value="hybrid">Hibrit</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Deneyim Seviyesi <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="experience_level"
                      defaultValue={editingJob?.experience_level || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Örn: 3-5 yıl"
                      required
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İş Açıklaması
                    <span className="ml-2 text-xs text-gray-500">
                      ({selectedLanguage === 'tr' ? 'Türkçe' : selectedLanguage === 'en' ? 'English' : 'Español'})
                    </span>
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <textarea
                    value={formDescriptions[selectedLanguage] || ''}
                    onChange={(e) => setFormDescriptions(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={selectedLanguage === 'tr' ? 'İş pozisyonunun detaylı açıklaması...' : selectedLanguage === 'en' ? 'Detailed description of the job position...' : 'Descripción detallada del puesto de trabajo...'}
                    required
                  />
                </div>

                {/* Responsibilities */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sorumluluklar
                    <span className="ml-2 text-xs text-gray-500">
                      ({selectedLanguage === 'tr' ? 'Türkçe' : selectedLanguage === 'en' ? 'English' : 'Español'})
                    </span>
                  </label>
                  {responsibilities[selectedLanguage].map((responsibility, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={responsibility}
                        onChange={(e) => updateListItem(setResponsibilities, index, e.target.value, selectedLanguage)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={selectedLanguage === 'tr' ? 'Sorumluluk açıklaması...' : selectedLanguage === 'en' ? 'Responsibility description...' : 'Descripción de responsabilidad...'}
                      />
                      {responsibilities[selectedLanguage].length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeListItem(setResponsibilities, index)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addListItem(setResponsibilities)}
                    className="text-primary-600 hover:text-primary-700 text-sm"
                  >
                    {selectedLanguage === 'tr' ? '+ Sorumluluk Ekle' : selectedLanguage === 'en' ? '+ Add Responsibility' : '+ Agregar Responsabilidad'}
                  </button>
                </div>

                {/* Required Qualifications */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gerekli Nitelikler
                    <span className="ml-2 text-xs text-gray-500">
                      ({selectedLanguage === 'tr' ? 'Türkçe' : selectedLanguage === 'en' ? 'English' : 'Español'})
                    </span>
                  </label>
                  {qualifications[selectedLanguage].map((qualification, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={qualification}
                        onChange={(e) => updateListItem(setQualifications, index, e.target.value, selectedLanguage)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={selectedLanguage === 'tr' ? 'Gerekli nitelik...' : selectedLanguage === 'en' ? 'Required qualification...' : 'Cualificación requerida...'}
                      />
                      {qualifications[selectedLanguage].length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeListItem(setQualifications, index)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addListItem(setQualifications)}
                    className="text-primary-600 hover:text-primary-700 text-sm"
                  >
                    {selectedLanguage === 'tr' ? '+ Nitelik Ekle' : selectedLanguage === 'en' ? '+ Add Qualification' : '+ Agregar Cualificación'}
                  </button>
                </div>

                {/* Advantages */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Avantajlar (Tercih Edilen)
                    <span className="ml-2 text-xs text-gray-500">
                      ({selectedLanguage === 'tr' ? 'Türkçe' : selectedLanguage === 'en' ? 'English' : 'Español'})
                    </span>
                  </label>
                  {advantages[selectedLanguage].map((advantage, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={advantage}
                        onChange={(e) => updateListItem(setAdvantages, index, e.target.value, selectedLanguage)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={selectedLanguage === 'tr' ? 'Avantaj açıklaması...' : selectedLanguage === 'en' ? 'Advantage description...' : 'Descripción de ventaja...'}
                      />
                      {advantages[selectedLanguage].length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeListItem(setAdvantages, index)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addListItem(setAdvantages)}
                    className="text-primary-600 hover:text-primary-700 text-sm"
                  >
                    {selectedLanguage === 'tr' ? '+ Avantaj Ekle' : selectedLanguage === 'en' ? '+ Add Advantage' : '+ Agregar Ventaja'}
                  </button>
                </div>

                {/* Active Status */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      defaultChecked={editingJob?.is_active ?? true}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">İlanı aktif olarak yayınla</span>
                  </label>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseJobForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Kaydediliyor...' : editingJob ? 'Güncelle' : 'Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Application Details Modal */}
      {showApplicationDetails && selectedApplication && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowApplicationDetails(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                Başvuru Detayları
              </h2>
              <button
                onClick={() => setShowApplicationDetails(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Applicant Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <User className="h-5 w-5 mr-2" />
                    Başvuru Sahibi Bilgileri
                  </h3>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Ad Soyad</label>
                      <p className="text-gray-900">{selectedApplication.full_name}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">E-posta</label>
                      <a 
                        href={`mailto:${selectedApplication.email}`}
                        className="text-primary-600 hover:text-primary-700 flex items-center"
                      >
                        <Mail className="h-4 w-4 mr-1" />
                        {selectedApplication.email}
                      </a>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Telefon</label>
                      <a 
                        href={`tel:${selectedApplication.phone}`}
                        className="text-primary-600 hover:text-primary-700 flex items-center"
                      >
                        <Phone className="h-4 w-4 mr-1" />
                        {selectedApplication.phone}
                      </a>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Deneyim Seviyesi</label>
                      <p className="text-gray-900">{selectedApplication.experience_level}</p>
                    </div>
                    
                    {(selectedApplication.linkedin_profile || selectedApplication.portfolio_github) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sosyal Profiller</label>
                        <div className="space-y-2">
                          {selectedApplication.linkedin_profile && (
                            <a 
                              href={selectedApplication.linkedin_profile}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              LinkedIn Profili
                            </a>
                          )}
                          
                          {selectedApplication.portfolio_github && (
                            <a 
                              href={selectedApplication.portfolio_github}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors ml-2"
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              GitHub/Portfolio
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <Briefcase className="h-5 w-5 mr-2" />
                    Pozisyon Bilgileri
                  </h3>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Pozisyon</label>
                      <p className="text-gray-900">{selectedApplication.job?.title?.tr}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Başvuru Tarihi</label>
                      <p className="text-gray-900 flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(selectedApplication.created_at || selectedApplication.createdAt || '').toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    
                    
                    {selectedApplication.cv_file_url && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">CV Dosyası</label>
                        <a 
                          href={selectedApplication.cv_file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-2 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          CV İndir
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Cover Letter */}
              {selectedApplication.cover_letter && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Ön Yazı</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {selectedApplication.cover_letter}
                    </p>
                  </div>
                </div>
              )}

            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowApplicationDetails(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Career;
